import { useState } from 'preact/hooks';
import { actionsSignal } from '../../state/ai.store';
import { activeContactSignal, isLoadingActionsSignal } from '../../state/contacts.store';
import { selectedKanbanTaskSignal } from '../../state/kanban.store';
import { apiFetch } from '../../services/api';
import { showToast } from '../../state/ui.store';
import type { ActionItem } from '../../types/ai';
import { getMemberColor } from '../../utils/colorUtils';
import { buildGoogleCalendarUrl } from '../../utils/calendarUtils';
import { User, UserCheck, Users, Calendar, ChevronDown, ChevronRight, CheckSquare, CalendarPlus, Clock, MapPin, ExternalLink } from 'lucide-preact';

export function ActionsTab() {
  const actions = actionsSignal.value;
  const activeContact = activeContactSignal.value;
  const isLoadingActions = isLoadingActionsSignal.value;
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [mobileTab, setMobileTab] = useState<'mine' | 'contact'>('mine');

  if (isLoadingActions) {
    return (
      <div id="tab-actions" class="panel-container tab-content p-6 space-y-4 animate-pulse">
        <div class="h-10 bg-slate-900/80 border border-slate-800 rounded-xl w-full"></div>
        <div class="space-y-3">
          <div class="h-20 bg-slate-900/90 border border-slate-800 rounded-xl"></div>
          <div class="h-20 bg-slate-900/90 border border-slate-800 rounded-xl"></div>
          <div class="h-20 bg-slate-900/90 border border-slate-800 rounded-xl"></div>
        </div>
      </div>
    );
  }

  const handleToggleAction = async (action: ActionItem) => {
    if (!action || !action.id) {
      showToast('ID da ação inválido.', 'error');
      return;
    }
    const newStatus = action.status === 'completed' ? 'pending' : 'completed';
    try {
      await apiFetch(`/ai/actions/${action.id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      }).catch(() => apiFetch(`/ai/actions/${action.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus })
      }));
      actionsSignal.value = actionsSignal.value.map(a =>
        a.id === action.id ? { ...a, status: newStatus } : a
      );
      showToast(`Ação marcada como ${newStatus === 'completed' ? 'concluída' : 'pendente'}!`, 'success');
    } catch (err: any) {
      showToast(`Erro ao atualizar ação: ${err.message}`, 'error');
    }
  };

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  if (!activeContact) return null;

  const isGroup = Boolean(activeContact.is_group || activeContact.phone_number?.includes('@g.us'));

  // Categorize actions into My Actions vs Contact/Other Actions
  const isMyActionKey = (assignee?: string | null) => {
    if (!assignee) return true;
    const norm = assignee.toLowerCase().trim();
    if (norm === '') return true;

    const myKeywords = ['você', 'voce', 'admin', 'eu', 'mim'];
    return myKeywords.some(key => norm.includes(key));
  };

  const myActions: ActionItem[] = [];
  const othersActionsMap: Record<string, ActionItem[]> = {};

  actions.forEach(act => {
    if (isMyActionKey(act.assignee)) {
      myActions.push(act);
    } else {
      const ownerName = act.assignee || activeContact.name;
      if (!othersActionsMap[ownerName]) {
        othersActionsMap[ownerName] = [];
      }
      othersActionsMap[ownerName].push(act);
    }
  });

  const formatDueDate = (dateStr?: string | null) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  /* =======================================================
   * SCENARIO A: INDIVIDUAL CHAT (1-ON-1)
   * Desktop: Split Screen (Side-by-Side 2 Columns)
   * Mobile: Responsive Tab Switcher (Mine vs Contact)
   * ======================================================= */
  if (!isGroup) {
    const contactActions = Object.values(othersActionsMap).flat();

    return (
      <div id="tab-actions" class="panel-container tab-content space-y-4">
        {actions.length === 0 ? (
          <div class="p-8 text-center text-xs text-slate-400 font-medium bg-slate-900/60 rounded-2xl border border-slate-800">
            Nenhuma ação pendente identificada pela IA para {activeContact.name}.
          </div>
        ) : (
          <div>
            {/* MOBILE TAB SWITCHER (Shown only on small screens) */}
            <div class="flex items-center gap-2 mb-4 p-1 bg-slate-900/80 rounded-xl border border-slate-800 md:hidden">
              <button
                onClick={() => setMobileTab('mine')}
                class={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer ${
                  mobileTab === 'mine'
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/40 shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <User class="w-3.5 h-3.5" />
                <span>Minhas Ações ({myActions.length})</span>
              </button>
              <button
                onClick={() => setMobileTab('contact')}
                class={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer ${
                  mobileTab === 'contact'
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-400/40 shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <UserCheck class="w-3.5 h-3.5" />
                <span>{activeContact.name} ({contactActions.length})</span>
              </button>
            </div>

            {/* MOBILE VIEW (Single Column based on selected tab) */}
            <div class="block md:hidden">
              {mobileTab === 'mine' && (
                <ColumnActionList
                  title="Minhas Ações (Você)"
                  icon={<User class="w-4 h-4 text-cyan-400" />}
                  actions={myActions}
                  isMine={true}
                  defaultOwner="Você"
                  onToggle={handleToggleAction}
                  formatDueDate={formatDueDate}
                  accentClass="border-cyan-500/30 bg-cyan-950/20"
                />
              )}
              {mobileTab === 'contact' && (
                <ColumnActionList
                  title={`Ações de ${activeContact.name}`}
                  icon={<UserCheck class="w-4 h-4 text-purple-400" />}
                  actions={contactActions}
                  isMine={false}
                  defaultOwner={activeContact.name}
                  onToggle={handleToggleAction}
                  formatDueDate={formatDueDate}
                  accentClass="border-purple-500/30 bg-purple-950/20"
                />
              )}
            </div>

            {/* DESKTOP VIEW (Side-by-Side 2 Columns) */}
            <div class="hidden md:grid md:grid-cols-2 md:gap-4 items-start">
              {/* Left Column: Minhas Acoes */}
              <ColumnActionList
                title="Minhas Ações (Você)"
                icon={<User class="w-4 h-4 text-cyan-400" />}
                actions={myActions}
                isMine={true}
                defaultOwner="Você"
                onToggle={handleToggleAction}
                formatDueDate={formatDueDate}
                accentClass="border-cyan-500/30 bg-cyan-950/20"
              />

              {/* Right Column: Acoes do Contato */}
              <ColumnActionList
                title={`Ações de ${activeContact.name}`}
                icon={<UserCheck class="w-4 h-4 text-purple-400" />}
                actions={contactActions}
                isMine={false}
                defaultOwner={activeContact.name}
                onToggle={handleToggleAction}
                formatDueDate={formatDueDate}
                accentClass="border-purple-500/30 bg-purple-950/20"
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  /* =======================================================
   * SCENARIO B: GROUP CHAT
   * Rule:
   * 1. Minhas Ações ALWAYS in 1st place (with special cyan glow header), even if 0 tasks
   * 2. Members with >= 3 tasks get individual collapsible box
   * 3. Members with < 3 tasks are grouped in "Outros" box with member color badges
   * ======================================================= */
  const standaloneGroups: { ownerName: string; actions: ActionItem[] }[] = [];
  const othersGroupActions: ActionItem[] = [];

  Object.entries(othersActionsMap).forEach(([ownerName, itemArr]) => {
    if (itemArr.length >= 3) {
      standaloneGroups.push({ ownerName, actions: itemArr });
    } else {
      othersGroupActions.push(...itemArr);
    }
  });

  return (
    <div id="tab-actions" class="panel-container tab-content space-y-4">
      <div class="space-y-4">

        {/* SECTION 1: MINHAS AÇÕES (ALWAYS FIRST PLACE WITH SPECIAL CYAN GLOW HIGHLIGHT) */}
        <div class="space-y-2">
          <div
            onClick={() => toggleGroup('my_actions')}
            class="action-group-header my-actions"
          >
            <div class="flex items-center gap-2">
              <User class="w-4 h-4 text-cyan-300 drop-shadow-[0_0_6px_rgba(6,182,212,0.6)]" />
              <h3 class="text-xs font-black text-cyan-200 uppercase tracking-wider">
                Minhas Ações (Você / Admin)
              </h3>
              <span class="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-400/40 shadow-sm">
                {myActions.filter(a => a.status === 'pending').length} pendente(s)
              </span>
            </div>
            <div class="text-slate-400">
              {collapsedGroups['my_actions'] ? (
                <ChevronRight class="w-4 h-4" />
              ) : (
                <ChevronDown class="w-4 h-4" />
              )}
            </div>
          </div>

          {!collapsedGroups['my_actions'] && (
            <div class="space-y-2.5 pl-1 sm:pl-2">
              {myActions.length === 0 ? (
                <div class="p-3 text-center text-xs text-cyan-300/80 font-medium bg-cyan-950/20 border border-cyan-500/20 rounded-xl">
                  Nenhuma pendência atribuída a você neste grupo. ✨
                </div>
              ) : (
                myActions.map(action => (
                  <ActionCardItem
                    key={action.id}
                    action={action}
                    isMine={true}
                    ownerName="Você"
                    onToggle={handleToggleAction}
                    formatDueDate={formatDueDate}
                  />
                ))
              )}
            </div>
          )}
        </div>

        {/* SECTION 2: INDIVIDUAL COLLAPSIBLE GROUPS (For Members with >= 3 Tasks) */}
        {standaloneGroups.map(({ ownerName, actions: groupItems }) => {
          const groupKey = `standalone_${ownerName}`;
          const isCollapsed = Boolean(collapsedGroups[groupKey]);
          const pendingCount = groupItems.filter(a => a.status === 'pending').length;
          const memberColor = getMemberColor(ownerName);

          return (
            <div key={ownerName} class="space-y-2">
              <div
                onClick={() => toggleGroup(groupKey)}
                class="action-group-header"
              >
                <div class="flex items-center gap-2">
                  <UserCheck class={`w-4 h-4 ${memberColor.text}`} />
                  <h3 class={`text-xs font-bold uppercase tracking-wider ${memberColor.text}`}>
                    Ações de {ownerName}
                  </h3>
                  <span class={`px-2 py-0.5 rounded-full text-[10px] font-bold ${memberColor.badge}`}>
                    {pendingCount} pendente(s)
                  </span>
                </div>
                <div class="text-slate-400">
                  {isCollapsed ? (
                    <ChevronRight class="w-4 h-4" />
                  ) : (
                    <ChevronDown class="w-4 h-4" />
                  )}
                </div>
              </div>

              {!isCollapsed && (
                <div class="space-y-2.5 pl-1 sm:pl-2">
                  {groupItems.map(action => (
                    <ActionCardItem
                      key={action.id}
                      action={action}
                      isMine={false}
                      ownerName={ownerName}
                      onToggle={handleToggleAction}
                      formatDueDate={formatDueDate}
                      hideOwnerBadge={true}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* SECTION 3: "OUTROS" GROUP (For Members with < 3 Tasks) */}
        {othersGroupActions.length > 0 && (
          <div class="space-y-2">
            <div
              onClick={() => toggleGroup('others_collective')}
              class="action-group-header"
            >
              <div class="flex items-center gap-2">
                <Users class="w-4 h-4 text-purple-400" />
                <h3 class="text-xs font-bold text-purple-200 uppercase tracking-wider">
                  Outros Membros do Grupo
                </h3>
                <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-500/20 text-purple-300 border border-purple-400/30">
                  {othersGroupActions.filter(a => a.status === 'pending').length} pendente(s)
                </span>
              </div>
              <div class="text-slate-400">
                {collapsedGroups['others_collective'] ? (
                  <ChevronRight class="w-4 h-4" />
                ) : (
                  <ChevronDown class="w-4 h-4" />
                )}
              </div>
            </div>

            {!collapsedGroups['others_collective'] && (
              <div class="space-y-2.5 pl-1 sm:pl-2">
                {othersGroupActions.map(action => {
                  const ownerName = action.assignee || activeContact.name;
                  return (
                    <ActionCardItem
                      key={action.id}
                      action={action}
                      isMine={false}
                      ownerName={ownerName}
                      onToggle={handleToggleAction}
                      formatDueDate={formatDueDate}
                      hideOwnerBadge={false}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {actions.length === 0 && (
          <div class="p-6 text-center text-xs text-slate-400 font-medium bg-slate-900/40 rounded-2xl border border-slate-800/60">
            Nenhuma outra ação pendente identificada pela IA neste grupo.
          </div>
        )}

      </div>
    </div>
  );
}

/* =======================================================
 * HELPER COMPONENTS
 * ======================================================= */

interface ColumnActionListProps {
  title: string;
  icon: any;
  actions: ActionItem[];
  isMine: boolean;
  defaultOwner: string;
  onToggle: (action: ActionItem) => void;
  formatDueDate: (dateStr?: string | null) => string | null;
  accentClass: string;
}

function ColumnActionList({
  title,
  icon,
  actions,
  isMine,
  defaultOwner,
  onToggle,
  formatDueDate,
  accentClass
}: ColumnActionListProps) {
  const pendingCount = actions.filter(a => a.status === 'pending').length;

  return (
    <div class={`p-3 sm:p-4 rounded-2xl border ${accentClass} space-y-3`}>
      <div class="flex items-center justify-between pb-2 border-b border-slate-800/80">
        <div class="flex items-center gap-2">
          {icon}
          <h3 class="text-xs font-bold text-slate-200 uppercase tracking-wider">{title}</h3>
        </div>
        <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-900 text-slate-300 border border-slate-800">
          {pendingCount}
        </span>
      </div>

      {actions.length === 0 ? (
        <div class="py-6 text-center text-[11px] text-slate-500 italic">
          Nenhuma pendência nesta categoria.
        </div>
      ) : (
        <div class="space-y-2.5">
          {actions.map(action => (
            <ActionCardItem
              key={action.id}
              action={action}
              isMine={isMine}
              ownerName={action.assignee || defaultOwner}
              onToggle={onToggle}
              formatDueDate={formatDueDate}
              hideOwnerBadge={true}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface ActionCardItemProps {
  action: ActionItem;
  isMine: boolean;
  ownerName: string;
  onToggle: (action: ActionItem) => void;
  formatDueDate: (dateStr?: string | null) => string | null;
  hideOwnerBadge?: boolean;
}

function ActionCardItem({
  action,
  isMine,
  ownerName,
  onToggle,
  formatDueDate,
  hideOwnerBadge = false
}: ActionCardItemProps) {
  const isCompleted = action.status === 'completed';
  const dueDateFormatted = formatDueDate(action.due_date);
  const memberColor = getMemberColor(ownerName);
  const activeContact = activeContactSignal.value;

  const isAppointment = Boolean(action.is_appointment || action.event_start_time);
  const calendarUrl = buildGoogleCalendarUrl(action, activeContact?.name);

  const formatEventTimeSpan = (startStr?: string | null, endStr?: string | null) => {
    if (!startStr) return null;
    try {
      const startDate = new Date(startStr.replace(' ', 'T'));
      if (isNaN(startDate.getTime())) return startStr;
      
      const datePart = startDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const timePart = startDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

      if (endStr) {
        const endDate = new Date(endStr.replace(' ', 'T'));
        if (!isNaN(endDate.getTime())) {
          const endTimePart = endDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
          return `${datePart} às ${timePart} - ${endTimePart}`;
        }
      }
      return `${datePart} às ${timePart}`;
    } catch {
      return startStr;
    }
  };

  const eventTimeFormatted = formatEventTimeSpan(action.event_start_time, action.event_end_time);

  const priorityColor =
    action.priority === 'high'
      ? 'bg-rose-500/20 text-rose-300 border-rose-500/30'
      : action.priority === 'medium'
      ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
      : 'bg-slate-800 text-slate-300 border-slate-700';

  return (
    <div
      onClick={() => {
        selectedKanbanTaskSignal.value = action;
      }}
      class={`action-card relative cursor-pointer hover:border-cyan-500/50 hover:shadow-lg transition-all ${isMine ? 'my-card' : ''} ${isCompleted ? 'completed' : ''}`}
      title="Clique para abrir detalhes completos da tarefa"
    >
      {/* Priority Badge on TOP RIGHT Corner */}
      <div class="absolute top-3 right-3 z-10 flex items-center gap-1.5">
        {isAppointment && (
          <span class="px-2 py-0.5 rounded text-[9px] font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-400/40 uppercase tracking-wider flex items-center gap-1">
            <CalendarPlus class="w-2.5 h-2.5 text-cyan-400" />
            <span>Compromisso</span>
          </span>
        )}
        <span class={`px-2 py-0.5 rounded text-[9px] font-bold border uppercase tracking-wider ${priorityColor}`}>
          {action.priority || 'média'}
        </span>
      </div>

      <div class="flex items-start gap-3 min-w-0 flex-1 pr-24">
        <input
          type="checkbox"
          checked={isCompleted}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(action);
          }}
          onChange={() => {}}
          class="mt-1 w-4 h-4 rounded bg-slate-950 border-slate-800 text-cyan-500 focus:ring-cyan-500 cursor-pointer min-h-[20px] min-w-[20px]"
        />
        <div class="space-y-1.5 min-w-0 flex-1">
          {/* Header Row: Member Badge (optional) -> Due Date / Event Time Badge -> Task Title */}
          <div class="flex flex-wrap items-center gap-2">
            {!hideOwnerBadge && (
              <span class={`action-assignee-badge ${isMine ? 'user' : ''}`}>
                {isMine ? (
                  <>
                    <User class="w-3 h-3 text-cyan-400" />
                    <span>Você</span>
                  </>
                ) : (
                  <span class={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold border ${memberColor.badge}`}>
                    <UserCheck class="w-3 h-3" />
                    <span>{ownerName}</span>
                  </span>
                )}
              </span>
            )}

            {/* Event Time or Due Date Badge */}
            {eventTimeFormatted ? (
              <span class="px-2 py-0.5 rounded text-[10px] font-semibold bg-cyan-950/80 text-cyan-300 border border-cyan-500/40 flex items-center gap-1">
                <Clock class="w-3 h-3 text-cyan-400" />
                <span>{eventTimeFormatted}</span>
              </span>
            ) : dueDateFormatted ? (
              <span class="action-due-date">
                <Calendar class="w-3 h-3" />
                <span>Prazo: {dueDateFormatted}</span>
              </span>
            ) : null}

            {/* Task Title */}
            <h4 class={`text-xs font-bold ${isCompleted ? 'line-through text-slate-400' : 'text-slate-100'}`}>
              {action.title || action.description}
            </h4>
          </div>

          {/* Detailed Description Preview */}
          {action.detailed_description && (
            <p class="text-[11px] text-slate-300 font-medium line-clamp-2 leading-relaxed pt-0.5">
              {action.detailed_description}
            </p>
          )}

          {/* Location if present */}
          {action.location && (
            <div class="flex items-center gap-1 text-[11px] text-emerald-300 font-medium pt-0.5">
              <MapPin class="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
              <span>Local: {action.location}</span>
            </div>
          )}

          {action.description && action.title && (
            <p class="text-[11px] text-slate-400 leading-relaxed">{action.description}</p>
          )}

          {/* Add to Google Calendar Button (for appointments or actions with time/date) */}
          {(isAppointment || action.event_start_time || action.due_date) && !isCompleted && (
            <div class="pt-1">
              <a
                href={calendarUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-900/90 hover:bg-slate-800 border border-cyan-500/40 hover:border-cyan-400 text-cyan-300 text-[10px] font-bold shadow-xs transition-all hover:scale-102"
              >
                <CalendarPlus class="w-3.5 h-3.5 text-cyan-400" />
                <span>Adicionar ao Google Calendar 🗓️</span>
                <ExternalLink class="w-3 h-3 text-slate-400" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
